<!-- eslint-disable vue/no-mutating-props -->
<!-- 用户余额与积分调整的弹窗模块。表单状态由父页面集中管理。 -->
<template>
  <el-drawer
    :model-value="userManagerVisible"
    :title="selectedConsumerUser ? `用户管理 · ${selectedConsumerUser.nickname || selectedConsumerUser.id}` : '用户管理'"
    size="48%"
    @update:model-value="emit('update:userManagerVisible', $event)"
  >
    <template v-if="selectedConsumerUser">
      <div class="user-drawer">
        <section class="user-drawer__summary">
          <div class="user-avatar">{{ getUserInitial(selectedConsumerUser) }}</div>
          <div class="user-metrics">
            <div><span>余额</span><strong>{{ formatAmount(selectedConsumerUser.balance ?? 0) }}</strong></div>
            <div><span>积分</span><strong>{{ selectedConsumerUser.points ?? 0 }}</strong></div>
            <div><span>订单数</span><strong>{{ selectedConsumerUser.orders ?? 0 }}</strong></div>
          </div>
        </section>

        <el-tabs v-model="activeUserTab" class="user-tabs">
          <el-tab-pane label="基本信息" name="basic">
            <table class="info-table">
              <tbody>
                <tr><th>用户 ID</th><td>{{ selectedConsumerUser.id }}</td></tr>
                <tr><th>昵称</th><td>{{ selectedConsumerUser.nickname || '--' }}</td></tr>
                <tr><th>手机号</th><td>{{ selectedConsumerUser.phone || '--' }}</td></tr>
                <tr><th>会员等级</th><td>{{ getUserLevelText(selectedConsumerUser.levelName, selectedConsumerUser.level) }}</td></tr>
                <tr><th>管理员权限</th><td>{{ getUserAdminPermissionText(selectedConsumerUser) }}</td></tr>
                <tr><th>注册时间</th><td>{{ selectedConsumerUser.createdAt || '--' }}</td></tr>
                <tr><th>账号状态</th><td><span class="state-pill">{{ getUserStatusText(selectedConsumerUser.status) }}</span></td></tr>
              </tbody>
            </table>
          </el-tab-pane>

          <el-tab-pane label="管理员权限" name="adminPermission">
            <section class="manager-panel">
              <div>
                <span>小程序订单管理</span>
                <strong>{{ getUserAdminPermissionText(selectedConsumerUser) }}</strong>
                <small>开启后，该用户登录小程序可进入“我的-订单管理”。</small>
              </div>
              <el-button
                :type="hasUserAdminOrderPermission(selectedConsumerUser) ? 'danger' : 'primary'"
                :loading="userAdminPermissionSubmitting"
                :disabled="!canEditUserAdminPermission"
                plain
                @click="toggleUserAdminPermission"
              >
                {{ hasUserAdminOrderPermission(selectedConsumerUser) ? '关闭管理员' : '开启管理员' }}
              </el-button>
            </section>
          </el-tab-pane>

          <el-tab-pane v-if="canViewMember" label="会员信息" name="member">
            <div v-loading="loadingMemberProfile" class="member-panel">
              <template v-if="selectedMemberProfile">
                <table class="info-table">
                  <tbody>
                    <tr><th>会员档案 ID</th><td>{{ selectedMemberProfile.id }}</td></tr>
                    <tr><th>会员等级</th><td>{{ selectedMemberProfile.levelName || selectedMemberProfile.levelCode }}</td></tr>
                    <tr><th>成长值</th><td>{{ selectedMemberProfile.growthValue }}</td></tr>
                    <tr><th>可用积分</th><td>{{ selectedMemberProfile.points }}</td></tr>
                    <tr><th>会员余额</th><td>{{ formatAmount(selectedMemberProfile.balance) }}</td></tr>
                    <tr><th>累计积分</th><td>{{ selectedMemberProfile.totalPoints }}</td></tr>
                    <tr><th>累计充值</th><td>{{ formatAmount(selectedMemberProfile.totalRecharge) }}</td></tr>
                    <tr><th>入会时间</th><td>{{ selectedMemberProfile.joinedAt || '--' }}</td></tr>
                    <tr><th>会员到期时间</th><td>{{ selectedMemberProfile.memberExpireAt || '长期有效' }}</td></tr>
                    <tr><th>更新时间</th><td>{{ selectedMemberProfile.updatedAt || '--' }}</td></tr>
                  </tbody>
                </table>
                <div v-if="canEditMember" class="member-actions">
                  <el-button type="primary" @click="emit('openMemberEditor')">编辑会员档案</el-button>
                  <el-button type="danger" plain @click="emit('removeMemberProfile')">删除会员档案</el-button>
                </div>
              </template>
              <el-empty v-else description="该用户暂无会员档案">
                <el-button v-if="canEditMember" type="primary" @click="emit('openMemberEditor')">创建会员档案</el-button>
              </el-empty>
            </div>
          </el-tab-pane>

          <el-tab-pane label="积分明细" name="pointsLogs">
            <table class="detail-table">
              <thead><tr><th>类型</th><th class="is-right">变动积分</th><th class="is-right">变动后积分</th><th>时间</th></tr></thead>
              <tbody>
                <tr v-for="item in pointsPage?.list ?? []" :key="item.id">
                  <td>{{ getPointsLogTypeText(item.type) }}</td>
                  <td class="is-right">{{ formatSignedNumber(item.changePoints) }}</td>
                  <td class="is-right">{{ item.pointsAfter ?? 0 }}</td>
                  <td>{{ item.createdAt || '--' }}</td>
                </tr>
                <tr v-if="!(pointsPage?.list.length) && !loadingPointsLogs"><td colspan="4"><el-empty description="暂无积分明细" /></td></tr>
              </tbody>
            </table>
          </el-tab-pane>

          <el-tab-pane label="余额明细" name="walletLogs">
            <table class="detail-table">
              <thead><tr><th>类型</th><th class="is-right">变动金额</th><th class="is-right">变动后余额</th><th>时间</th></tr></thead>
              <tbody>
                <tr v-for="item in walletPage?.list ?? []" :key="item.id">
                  <td>{{ getWalletLogTypeText(item.type) }}</td>
                  <td class="is-right">{{ formatSignedAmount(item.changeAmount) }}</td>
                  <td class="is-right">{{ formatAmount(item.balanceAfter ?? 0) }}</td>
                  <td>{{ item.createdAt || '--' }}</td>
                </tr>
                <tr v-if="!(walletPage?.list.length) && !loadingWalletLogs"><td colspan="4"><el-empty description="暂无余额明细" /></td></tr>
              </tbody>
            </table>
          </el-tab-pane>
        </el-tabs>
      </div>
    </template>
  </el-drawer>

  <el-dialog
    :model-value="userBalanceEditorVisible"
    :title="selectedConsumerUser ? `余额调整 · ${selectedConsumerUser.nickname || selectedConsumerUser.id}` : '余额调整'"
    width="560px"
    @update:model-value="emit('update:userBalanceEditorVisible', $event)"
  >
    <div class="detail-grid detail-grid--single">
      <article class="detail-card">
        <div class="detail-actions detail-actions--store">
          <GuidedField label="调整金额" hint="金额单位为元；正数增加余额，负数扣减余额。" required>
            <el-input v-model.number="userBalanceForm.amount" type="number" step="0.01" placeholder="如 10，表示增加 ¥10" />
          </GuidedField>
          <GuidedField label="调整备注" hint="说明本次调账原因，方便后续核对。" wide>
            <el-input v-model.trim="userBalanceForm.remark" placeholder="如 售后补偿" />
          </GuidedField>
        </div>
      </article>
    </div>
    <template #footer>
      <el-button @click="emit('update:userBalanceEditorVisible', false)">取消</el-button>
      <el-button type="primary" :loading="userBalanceSubmitting" @click="emit('submitUserBalance')">保存</el-button>
    </template>
  </el-dialog>

  <el-dialog
    :model-value="userPointsEditorVisible"
    :title="selectedConsumerUser ? `积分调整 · ${selectedConsumerUser.nickname || selectedConsumerUser.id}` : '积分调整'"
    width="560px"
    @update:model-value="emit('update:userPointsEditorVisible', $event)"
  >
    <div class="detail-grid detail-grid--single">
      <article class="detail-card">
        <div class="detail-actions detail-actions--store">
          <GuidedField label="调整积分" hint="正数增加积分，负数扣减积分。" required>
            <el-input v-model.number="userPointsForm.points" type="number" placeholder="如 100" />
          </GuidedField>
          <GuidedField label="调整备注" hint="说明本次积分调整原因，方便后续核对。" wide>
            <el-input v-model.trim="userPointsForm.remark" placeholder="如 活动补发" />
          </GuidedField>
        </div>
      </article>
    </div>
    <template #footer>
      <el-button @click="emit('update:userPointsEditorVisible', false)">取消</el-button>
      <el-button type="primary" :loading="userPointsSubmitting" @click="emit('submitUserPoints')">保存</el-button>
    </template>
  </el-dialog>

  <el-dialog
    :model-value="memberEditorVisible"
    :title="selectedConsumerUser ? `会员档案 · ${selectedConsumerUser.nickname || selectedConsumerUser.id}` : '会员档案'"
    width="680px"
    @update:model-value="emit('update:memberEditorVisible', $event)"
  >
    <div class="detail-grid">
      <GuidedField label="会员等级" required>
        <el-select v-model="memberForm.levelCode" placeholder="请选择会员等级">
          <el-option v-for="level in memberLevelOptions" :key="level.value" :label="level.label" :value="level.value" />
        </el-select>
      </GuidedField>
      <GuidedField label="成长值">
        <el-input-number v-model="memberForm.growthValue" :min="0" controls-position="right" />
      </GuidedField>
      <GuidedField label="可用积分">
        <el-input-number v-model="memberForm.points" :min="0" controls-position="right" />
      </GuidedField>
      <GuidedField label="会员余额" hint="金额单位为元，提交时转换为分。">
        <el-input-number v-model="memberForm.balanceYuan" :min="0" :precision="2" :step="0.01" controls-position="right" />
      </GuidedField>
      <GuidedField label="累计积分">
        <el-input-number v-model="memberForm.totalPoints" :min="0" controls-position="right" />
      </GuidedField>
      <GuidedField label="累计充值" hint="金额单位为元，提交时转换为分。">
        <el-input-number v-model="memberForm.totalRechargeYuan" :min="0" :precision="2" :step="0.01" controls-position="right" />
      </GuidedField>
      <GuidedField label="入会时间">
        <AdminDateTimePicker v-model="memberForm.joinedAt" placeholder="请选择入会时间" />
      </GuidedField>
      <GuidedField label="会员到期时间" hint="留空表示长期有效。">
        <AdminDateTimePicker v-model="memberForm.memberExpireAt" placeholder="请选择到期时间" />
      </GuidedField>
    </div>
    <template #footer>
      <el-button @click="emit('update:memberEditorVisible', false)">取消</el-button>
      <el-button type="primary" :loading="memberProfileSubmitting" @click="emit('submitMemberProfile')">保存</el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { computed, ref, toRefs } from 'vue'
import type { PageResult } from '@jiaofa/api-types'
import AdminDateTimePicker from '@/components/AdminDateTimePicker.vue'
import GuidedField from '@/components/GuidedField.vue'
import type { AdminMemberProfile, MemberLevelConfig } from '@/api/member'
import type { AdminConsumerUser, AdminPointsLog, AdminWalletLog, UserBalancePayload, UserPointsPayload } from '@/api/user'
import type { MemberProfileForm } from '../composables/useUserCenter'

const props = defineProps<{
  userManagerVisible: boolean
  userBalanceEditorVisible: boolean
  userPointsEditorVisible: boolean
  memberEditorVisible: boolean
  userBalanceSubmitting: boolean
  userPointsSubmitting: boolean
  memberProfileSubmitting: boolean
  userAdminPermissionSubmitting: boolean
  userBalanceForm: UserBalancePayload
  userPointsForm: UserPointsPayload
  memberForm: MemberProfileForm
  memberLevels?: MemberLevelConfig[]
  selectedConsumerUser?: AdminConsumerUser
  selectedMemberProfile?: AdminMemberProfile
  walletPage?: PageResult<AdminWalletLog>
  pointsPage?: PageResult<AdminPointsLog>
  loadingWalletLogs: boolean
  loadingPointsLogs: boolean
  loadingMemberProfile: boolean
  canViewMember: boolean
  canEditMember: boolean
  canEditUserAdminPermission: boolean
  formatAmount: (amountInFen: number) => string
  formatSignedAmount: (amountInFen?: number) => string
  formatSignedNumber: (value?: number) => string
  getUserLevelText: (levelName?: string, level?: string) => string
  getUserStatusText: (status?: string) => string
  getWalletLogTypeText: (type?: string) => string
  getPointsLogTypeText: (type?: string) => string
  hasUserAdminOrderPermission: (user?: AdminConsumerUser) => boolean
}>()

const emit = defineEmits<{
  'update:userManagerVisible': [value: boolean]
  'update:userBalanceEditorVisible': [value: boolean]
  'update:userPointsEditorVisible': [value: boolean]
  'update:memberEditorVisible': [value: boolean]
  openUserBalance: [user: AdminConsumerUser]
  openUserPoints: [user: AdminConsumerUser]
  openMemberEditor: []
  removeMemberProfile: []
  submitUserBalance: []
  submitUserPoints: []
  submitMemberProfile: []
  submitUserAdminPermission: [enabled: boolean]
}>()

const activeUserTab = ref('basic')

const {
  userManagerVisible,
  userBalanceEditorVisible,
  userPointsEditorVisible,
  memberEditorVisible,
  userBalanceSubmitting,
  userPointsSubmitting,
  memberProfileSubmitting,
  userAdminPermissionSubmitting,
  userBalanceForm,
  userPointsForm,
  memberForm,
  selectedConsumerUser,
  selectedMemberProfile,
  walletPage,
  pointsPage,
  loadingWalletLogs,
  loadingPointsLogs,
  loadingMemberProfile,
  canViewMember,
  canEditMember,
  canEditUserAdminPermission,
  formatAmount,
  formatSignedAmount,
  formatSignedNumber,
  getUserLevelText,
  getUserStatusText,
  getWalletLogTypeText,
  getPointsLogTypeText,
  hasUserAdminOrderPermission,
} = toRefs(props)

const memberLevelOptions = computed(() =>
  (props.memberLevels ?? []).map((level) => ({
    label: level.name || level.code,
    value: level.code,
  })),
)

function getUserInitial(user: AdminConsumerUser): string {
  return (user.nickname || user.phone || user.id).slice(0, 1)
}

function getUserAdminPermissionText(user?: AdminConsumerUser): string {
  return hasUserAdminOrderPermission.value(user) ? '已开启' : '未开启'
}

function toggleUserAdminPermission(): void {
  emit('submitUserAdminPermission', !hasUserAdminOrderPermission.value(selectedConsumerUser.value))
}
</script>

<style scoped lang="scss">
.user-drawer { display: flex; flex-direction: column; gap: 20px; }
.user-drawer__summary { display: flex; align-items: center; gap: 22px; }
.user-avatar { width: 72px; height: 72px; border-radius: 999px; display: grid; place-items: center; background: #d8dde5; color: #fff; font-size: 22px; font-weight: 700; }
.user-metrics { display: grid; grid-template-columns: repeat(3, minmax(0, 120px)); gap: 16px; }
.user-metrics div, .manager-panel div { min-width: 0; display: flex; flex-direction: column; gap: 6px; }
.user-metrics span, .manager-panel span, .manager-panel small { color: var(--jf-color-text-secondary); font-size: 13px; }
.user-metrics strong, .manager-panel strong { color: var(--jf-color-text-primary); font-size: 24px; line-height: 1.1; }
.user-tabs { margin-top: 4px; }
.member-panel { min-height: 220px; }
.member-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 16px; }
.info-table, .detail-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.info-table th, .info-table td, .detail-table th, .detail-table td { padding: 14px 16px; border: 1px solid var(--jf-color-border); text-align: left; }
.info-table th { width: 220px; background: rgba(252, 250, 246, .72); color: var(--jf-color-text-secondary); }
.detail-table thead { background: rgba(252, 250, 246, .72); color: var(--jf-color-text-secondary); }
.is-right { text-align: right; }
.state-pill { min-height: 24px; padding: 3px 10px; border: 1px solid rgba(69, 185, 124, .32); border-radius: 6px; color: var(--jf-color-success); font-weight: 700; }
.manager-panel { min-height: 120px; padding: 18px; border: 1px solid var(--jf-color-border); border-radius: 10px; display: flex; align-items: center; justify-content: space-between; gap: 16px; background: #fff; }
.detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.detail-grid--single { grid-template-columns: 1fr; }
.detail-card { padding: 16px; border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; }
.detail-actions--store { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
@media (max-width: 760px) {
  .detail-actions--store { grid-template-columns: 1fr; }
}
</style>
